import { useLayoutEffect, useRef, useState } from 'react';
import { useNamespaceCopy } from '@/i18n/ns';
import artifactPanelsCopy from '@/i18n/ns/artifact-panels';
import { WorkspaceMarkdown } from '@/workspace/markdown';
import { displayFields, readableKey } from './action-approval-copy';
import { privateActionField } from './action-field-privacy';
import type { ActionView } from './action-confirmation';
import { copyForTool, type ActionCopy } from './action-copy';

function DraftValue({ value, copy }: { value: unknown; copy: ActionCopy }) {
  if (typeof value === 'string') return <WorkspaceMarkdown text={value} />;
  if (Array.isArray(value))
    return (
      <ul className="pc-action-draft-list">
        {value.map((item, index) => (
          <li key={index}>
            <DraftValue value={item} copy={copy} />
          </li>
        ))}
      </ul>
    );
  if (value && typeof value === 'object')
    return (
      <dl className="pc-action-draft-properties">
        {Object.entries(value)
          .filter(([key]) => !privateActionField(key))
          .map(([key, child]) => (
            <div key={key}>
              <dt>{readableKey(key, copy)}</dt>
              <dd>
                <DraftValue value={child} copy={copy} />
              </dd>
            </div>
          ))}
      </dl>
    );
  return <span>{value == null ? '—' : String(value)}</span>;
}

/** The reviewed request of a native build: body type, six lines, then Show all. */
function ClampedRequest({ text }: { text: string }) {
  const copy = useNamespaceCopy(artifactPanelsCopy);
  const box = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [overflows, setOverflows] = useState(false);
  useLayoutEffect(() => {
    const element = box.current;
    if (element && !open) setOverflows(element.scrollHeight > element.clientHeight + 1);
  }, [text, open]);
  return (
    <>
      <div ref={box} className="pc-action-request" data-open={open ? 'true' : undefined}>
        <WorkspaceMarkdown text={text} />
      </div>
      {(overflows || open) && (
        <button
          type="button"
          className="pc-action-request-toggle"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          {open ? copy.requestShowLess : copy.requestShowAll}
        </button>
      )}
    </>
  );
}

/** A document in the existing side canvas, using the Skill draft hierarchy. */
export function ActionDraft({ action, copy: shared }: { action: ActionView; copy: ActionCopy }) {
  const copy = copyForTool(shared, action.tool);
  const fields = displayFields(action, copy).filter(
    ([key]) => !['name', 'title', 'description'].includes(key),
  );
  const description = action.args.description;
  return (
    <div className="pc-action-draft">
      {typeof description === 'string' && description && (
        <div className="pc-action-draft-description">
          <WorkspaceMarkdown text={description} />
        </div>
      )}
      {fields.map(([key, value]) => (
        <section className="pc-action-draft-section" key={key}>
          <h4>{readableKey(key, copy)}</h4>
          {action.tool === 'build_artifact' && key === 'task' && typeof value === 'string' ? (
            <ClampedRequest text={value} />
          ) : (
            <DraftValue value={value} copy={copy} />
          )}
        </section>
      ))}
    </div>
  );
}
